import dayjs from "dayjs";
import { Fragment } from "react";

import { getPeriod } from "@/lib/utils";
import { ForecastData } from "@/types";
import "@/pages/index/TrendHistory.scss";

export const TrendHistory = ({ forecast }: { forecast: ForecastData }) => {
	const formatDate = (date) => {
		return dayjs(date).format("DD.MM.YYYY");
	};
	const formatAmount = (value: number | null | undefined) =>
		value != null && Number.isFinite(value) ? value.toFixed(1) : "-";
	const currency = forecast.currency || (forecast.forecast.target === "BDSV-E2-8" ? "EUR" : "USD");

	return (
		<div className="forecast-detail">
			<div className="row">
				<div className="col-12 col-xl-8 trend-table-scroll">
					<table className="trend-table">
						<thead>
							<tr>
								<th rowSpan={2}>Trend date</th>
								{forecast?.trend_info?.map((item) => (
									<th key={item.shift} colSpan={4}>
										{getPeriod(forecast.forecast.granularity)} {item.shift}
									</th>
								))}
							</tr>
							<tr>
								{forecast?.trend_info?.map((item) => (
									<Fragment key={item.shift}>
										<th>Actual</th>
										<th>Forecast</th>
										<th title="Absolute price error">Error ({currency}/t)</th>
										<th>Margin ({currency}/t)</th>
									</Fragment>
								))}
							</tr>
						</thead>
						<tbody>
							{forecast?.trend_dates?.map((date) => (
								<tr key={date}>
									<td>{formatDate(date)}</td>
									{forecast?.trend_info?.map((item) => (
										<Fragment key={item.shift}>
											<td className={item.history[date]?.status}>
												{item.history[date]?.actual_trend || "-"}
											</td>
											<td className={item.history[date]?.status} title={item.history[date]?.model_label}>
												{item.history[date]?.forecasted_trend || "-"}
											</td>
											<td>{formatAmount(item.history[date]?.absolute_error)}</td>
											<td>{formatAmount(item.history[date]?.safety_margin)}</td>
										</Fragment>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="col-12 col-xl-4 historical-evaluation">
					{forecast.historical_evaluation && (
						<p>
							The historical evaluation of the <strong>Moirai</strong> model (started to be used
							from <strong>12.11.2024</strong>) for <strong>Week 4</strong> and{" "}
							<strong>Week 6</strong> trend shows <strong>the weighted average precision and recall</strong>,
							and the mean absolute price error (MAE).
							<br />
							Metrics calculation period: <strong>{forecast.historical_evaluation.period_start} - {forecast.historical_evaluation.period_end}</strong>
							{forecast.historical_evaluation.rows.map((row) => (
								<Fragment key={row.shift}>
									<br />
									Week {row.shift}: <strong>Precision = {row.precision.toFixed(1)}% Recall = {row.recall.toFixed(1)}% MAE = {row.mae.toFixed(1)} {currency}/t</strong>
								</Fragment>
							))}
							{forecast.market_model_label === "CatBoost (alpha 0.9)" && (
								<><br />These frozen metrics describe historical Moirai forecasts, not the current CatBoost model.</>
							)}
						</p>
					)}
					{forecast.forecast.target === "BDSV-E2-8" && (
						<p>
							The historical evaluation of the <strong>Moirai</strong> model (started to be used
							from <strong>12.11.2024</strong>) for <strong>Month 1</strong> and{" "}
							<strong>Month 2</strong> trend shows{" "}
							<strong>the weighted average precision and recall</strong>.
							<br />
							Metrics calculation period: <strong>01.02.2020 - 01.02.2025</strong>
							<br />
							Month 1: <strong>Precision = 83% Recall = 80%</strong>
							<br />
							Month 2: <strong>Precision = 86% Recall = 83%</strong>
						</p>
					)}
				</div>
				<div>
					<div className="notes">
						<p>
							<span className="good">green rows</span> - actual and forecasted trends match
							<br />
							<span className="warning">yellow rows</span> - actual and forecasted trends mismatch,
							but with nearby values (UP and NO, DOWN and NO)
							<br />
							<span className="bad">red rows</span> - actual and forecasted trends mismatch (UP and
							DOWN)
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
